import { Link } from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import { appName, company, formatNif } from '#app/utils/constants.ts'
import { Container } from '../components/layout/container'

export const Footer = () => {
	const { t } = useTranslation()

	const links = [
		{ name: t('footer.whatIBuild'), href: '/#what-i-build' },
		{ name: t('footer.howIWork'), href: '/#how-i-work' },
		{ name: t('footer.founder'), href: '/#founder' },
		{ name: t('footer.bookCall'), href: '/call' },
	]

	return (
		<footer className="relative overflow-hidden pt-fluid-18 pb-fluid-12">
			{/*
			  Soft blurred glow behind the footer — same idea as Durabl's Grad
			  cluster: large off-canvas ellipses, heavily blurred, low opacity.
			*/}
			<div
				aria-hidden
				className="pointer-events-none absolute inset-0 select-none"
			>
				<div className="absolute -right-[20%] bottom-[-40%] h-[28rem] w-[40rem] rounded-full bg-blue-200/50 blur-3xl dark:bg-blue-900/25" />
				<div className="absolute -right-[5%] bottom-[-20%] h-[20rem] w-[28rem] rounded-full bg-sky-100/60 blur-3xl dark:bg-sky-950/30" />
				<div className="absolute right-[15%] -bottom-8 h-48 w-72 rounded-full bg-indigo-100/40 blur-3xl dark:bg-indigo-950/20" />
			</div>

			<Container className="relative">
				<h2 id="footer-heading" className="sr-only">
					{t('footer.heading')}
				</h2>

				<div className="flex flex-col gap-fluid-16 lg:flex-row lg:justify-between lg:gap-20">
					{/* Brand + legal identity */}
					<div className="max-w-sm shrink-0">
						<p className="text-2xl font-bold tracking-tight text-foreground">
							{appName}
						</p>
						<p className="mt-3 text-base leading-relaxed text-muted-600">
							{t('footer.tagline')}
						</p>

						<address className="mt-6 space-y-1 not-italic text-sm leading-relaxed text-muted-600">
							<p>{company.legalName}</p>
							<p>{company.address.street}</p>
							<p>
								{company.address.postalCode} {company.address.city}
							</p>
							<p>{company.address.country}</p>
							<p className="pt-2 text-muted-500">
								NIPC {formatNif(company.nif)}
								<span className="mx-2 text-muted-300" aria-hidden>
									·
								</span>
								{t('footer.shareCapital', { amount: company.shareCapital })}
							</p>
						</address>
					</div>

					{/* Flat destinations — no Explore/Company taxonomy */}
					<nav aria-label={t('footer.heading')} className="lg:pt-1">
						<ul
							role="list"
							className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-x-8 sm:gap-y-3 lg:flex-col lg:items-end lg:gap-3"
						>
							{links.map(item => (
								<li key={item.href}>
									<Link
										to={item.href}
										className="text-sm text-muted-600 transition-colors hover:text-foreground"
									>
										{item.name}
									</Link>
								</li>
							))}
						</ul>
					</nav>
				</div>

				{/* Bottom bar */}
				<div className="mt-fluid-16 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
					<p className="text-sm text-muted-600">
						&copy; {new Date().getFullYear()} {appName}
					</p>
					<div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-600">
						<Link
							to="/privacy"
							className="transition-colors hover:text-foreground"
						>
							{t('footer.privacy')}
						</Link>
						<Link to="/tos" className="transition-colors hover:text-foreground">
							{t('footer.terms')}
						</Link>
						<a
							href={`mailto:${company.email}`}
							className="transition-colors hover:text-foreground"
						>
							{company.email}
						</a>
					</div>
				</div>
			</Container>
		</footer>
	)
}
